import * as React from "react";
import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";

import {
  Box,
  Button,
  Grid,
  MenuItem,
  Modal,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import {
  useCreatePartnerZonesMutation,
  useGetPartnerZonesQuery,
} from "../../Slices/roomNateApis";

import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "auto",
  maxHeight: "90%",
  overflowY: "auto",
  width: { xs: "90%", md: "650px" },
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ZoneManagementForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  push,
  remove,
  zonesData,
  isManualTab,
  isOtherTab,
}) => {
  return (
    <>
      {values.zones
        .map((zone, index) => ({ ...zone, index }))
        .filter((zone) =>
          isOtherTab ? !zone.visibleOnManualItem && !zone.visibleOnMakeMagic : isManualTab ? zone.visibleOnManualItem : zone.visibleOnMakeMagic
        )
        .map((zone, zoneDisplayIndex) => {
          const index = zone.index;

          return (
            <Box
              key={index}
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                position: "relative",
              }}
            >
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Grid item>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Zone {zoneDisplayIndex + 1}
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    type="button"
                    variant="contained"
                    size="small"
                    onClick={() => remove(index)}
                  >
                    X
                  </Button>
                </Grid>
              </Grid>

              <Grid container spacing={1} mb={2}>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    label="Label"
                    name={`zones[${index}].label`}
                    value={zone.label}
                    required
                    fullWidth
                    helperText={
                      touched?.zones?.[index]?.label &&
                      errors?.zones?.[index]?.label
                        ? errors.zones[index].label
                        : ""
                    }
                    error={Boolean(
                      touched?.zones?.[index]?.label &&
                        errors?.zones?.[index]?.label
                    )}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue(`zones[${index}].label`, e.target.value);
                    }}
                    onBlur={handleBlur}
                  />
                </Grid>

                {
                  !isManualTab && (
                    <Grid item xs={12}>
                      <TextField
                          select
                          label="Select Zone"
                          name={`zones[${index}].value`}
                          value={zone.value}
                          fullWidth
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            setFieldValue(`zones[${index}].value`, selectedValue);
                            if (selectedValue !== "custom") {
                              setFieldValue(`zones[${index}].customValue`, "");
                            }
                            
                          }}
                          onBlur={handleBlur}
                          helperText={
                            touched?.zones?.[index]?.value &&
                            errors?.zones?.[index]?.value
                              ? errors.zones[index].value
                              : ""
                          }
                          error={Boolean(
                            touched?.zones?.[index]?.value &&
                              errors?.zones?.[index]?.value
                          )}
                        >
                          <MenuItem value="">Select</MenuItem>
                          {zonesData?.filter((zoneOption) => zoneOption.isSupplementary !== true).map((zoneOption) => (
                            <MenuItem
                              key={zoneOption.value}
                              value={zoneOption.value}
                            >
                              {zoneOption.label}
                            </MenuItem>
                          ))}
                        </TextField>
                    </Grid>

                  )
                }

              </Grid>
            </Box>
          );
        })}

      <Button
        type="button"
        variant="outlined"
        onClick={() =>
          push({
            label: "",
            value: "",
            visibleOnMakeMagic: isOtherTab ? false : !isManualTab,
            visibleOnManualItem: isOtherTab ? false : isManualTab,
            partner: "",
          })
        }
      >
        Add Zone
      </Button>
    </>
  );
};

export default function AddPartnerZonesModal({
  editPartner,
  setEditPartner,
  zonesData,
}) {
  const { data: partnerZones, isFetching } = useGetPartnerZonesQuery(
    editPartner?._id,
    { skip: !editPartner?._id }
  );

  const [updatePartner, updateResultStats] = useCreatePartnerZonesMutation();
  const [open, setOpen] = React.useState(false);
  const [load, setLoad] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (e, newValue) => setTabValue(newValue);
  const handleOpen = React.useCallback(() => setOpen(true), []);
  const handleClose = React.useCallback(() => setOpen(false), []);

  // Track previous open state to detect modal close transition
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    // Only clear editPartner when modal actually CLOSES (was open, now closed)
    if (prevOpenRef.current && !open && editPartner?._id) {
      setEditPartner({});
    }
    prevOpenRef.current = open;
  }, [open, editPartner?._id, setEditPartner]);

  React.useEffect(() => {
    if (editPartner?._id) handleOpen();
  }, [editPartner?._id, handleOpen]);

  React.useEffect(() => {
    setLoad(updateResultStats.isLoading);
    if (updateResultStats.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => setSuccessAlert(false), 2000);
      handleClose();
      setEditPartner({});
    }
    if (updateResultStats.isError) {
      setErrorAlert(true);
      setTimeout(() => setErrorAlert(false), 2000);
    }
  }, [updateResultStats, handleClose, setEditPartner]);

  const formatZones = (zones) =>
    zones.map((zone) => {
      return {
        label: zone.label,
        value:  zone.value,
        visibleOnMakeMagic: zone.visibleOnMakeMagic,
        visibleOnManualItem: zone.visibleOnManualItem,
        partner: zone.partner,
        _id:zone._id
      };
    });

  const initialZones = partnerZones?.length ? formatZones(partnerZones) : [];

  const validationSchema = Yup.object().shape({
    zones: Yup.array().of(
      Yup.object().shape({
        label: Yup.string().required("Label is required"),
      })
    ),
  });

  const handleSubmit = async (values) => {
    const formatted = values.zones.map((zone) => ({
      label: zone.label,
      value: zone.value,
      visibleOnMakeMagic: zone.visibleOnMakeMagic,
      visibleOnManualItem: zone.visibleOnManualItem,
      partner: editPartner?._id,
      _id: zone?._id,
    }));
    await updatePartner({ id: editPartner._id, data: formatted });
  };

  if (isFetching) return <Typography>Loading...</Typography>;

  return (
    <>
      {
        successAlert && <SuccessAlert title="Zones updated successfully" onClose={() => setSuccessAlert(false)} />
      }
      {
        errorAlert && <ErrorAlert title="Error updating zones" onClose={() => setErrorAlert(false)} />
      }
      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Typography variant="h6" mb={2}>
            {editPartner?.name ? `${editPartner.name}'s Zones` : "Partner Zones"}
          </Typography>

          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Make Magic Zones" />
            <Tab label="Manual Item Zones" />
            <Tab label="Other Zones" />
          </Tabs>

          <Formik
            initialValues={{ zones: initialZones }}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <FieldArray name="zones">
                  {({ push, remove }) => (
                    <TabPanel value={tabValue} index={tabValue}>
                      <ZoneManagementForm
                        editPartner={editPartner}
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        push={push}
                        remove={remove}
                        zonesData={zonesData}
                        isManualTab={tabValue === 1}
                        isOtherTab={tabValue === 2}
                      />
                    </TabPanel>
                  )}
                </FieldArray>
                <Stack mt={2}>
                  <LoadingButton
                    loading={load}
                    variant="contained"
                    fullWidth
                    type="submit"
                  >
                    Save Zones
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}
